import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, SeverityPill, ProgressBar, fmt$, fmtN } from "@/components/primitives";
import RingGauge from "@/components/RingGauge";
import EmptyStateCard from "@/components/EmptyStateCard";

const OWN_LABEL = { rented: "rented", financed: "financed", owned: "owned", unknown: "unknown", sold: "sold", contracted: "contracted" };
const SIGNAL_TYPES = [
  ["gone_quiet", "Gone quiet"], ["active_dry", "Active but dry"], ["decay", "Decay"],
  ["under_contract", "Under contract"], ["orphan_asset", "Orphan asset"],
];
const SORTS = [["exposure", "Exposure"], ["kgshort", "Kg short"], ["daysdry", "Days dry"], ["name", "Company name"]];
const SNOOZE_REASONS = ["customer owns machine", "site closed or relocated", "seasonal", "price dispute", "switched to pods", "other"];
const SEV_META = [["crit", "Critical"], ["risk", "At risk"], ["watch", "Watch"]];
const PAGE = 20;

function StatCell({ label, value, color, first }) {
  return (
    <div style={{ padding: "0 22px", borderLeft: first ? "none" : "1px solid var(--ui-line-soft)" }}>
      <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 20, fontWeight: 500, letterSpacing: "-0.4px", color: color || "var(--ui-text-hi)", marginTop: 4 }}>{value}</div>
    </div>
  );
}
function Segmented({ active, onChange }) {
  const segs = [["all", "All"], ["rented", "Rented"], ["financed", "Financed"], ["owned", "Owned"]];
  return (
    <div style={{ display: "flex", gap: 2, background: "var(--ui-bg)", padding: 4, borderRadius: 10 }}>
      {segs.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} className="prose-i"
          style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
            background: active === k ? "var(--ac-indigo)" : "transparent", color: active === k ? "var(--ui-on-accent)" : "var(--ui-text-mid)" }}>{label}</button>
      ))}
    </div>
  );
}
function Chip({ label, onRemove }) {
  return (
    <span className="prose-i" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, background: "var(--ac-indigo-soft)", color: "var(--ac-indigo-hi)", border: "1px solid var(--ac-indigo)", borderRadius: 8, padding: "3px 8px" }}>
      {label}<button onClick={onRemove} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
    </span>
  );
}

// One site row (nested under a company, or the single row for a single-site company).
function SiteRow({ s, companyName, showSiteName, selected, onSelect, nav }) {
  const fa = s.flowActualKg != null ? s.flowActualKg : s.actualKg;
  const fe = s.flowExpectedKg != null ? s.flowExpectedKg : s.expectedKg;
  const pct = fe ? Math.min(100, (fa / fe) * 100) : 0;
  const company = s.companyFallback;
  return (
    <div className="lb-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "15px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
      {!company && <input type="checkbox" checked={selected} onChange={onSelect} style={{ accentColor: "var(--ac-indigo)", cursor: "pointer" }} />}
      {company && <span style={{ width: 13 }} />}
      <div style={{ flex: "0 0 240px" }}>
        <div className="prose-i" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ui-text-hi)" }}>
          {showSiteName ? (s.siteName || companyName) : companyName}{s.archived ? " · closed" : ""}
        </div>
        <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>{OWN_LABEL[s.ownership] || s.ownership}</div>
      </div>
      <div style={{ flex: "0 0 150px" }}><SeverityPill severity={s.severity} label={s.typeLabel} /></div>
      <div style={{ flex: 1, minWidth: 150, paddingRight: 14 }}>
        {company ? (
          <div className="prose-i" style={{ fontSize: 11.5, color: "var(--sv-watch)", fontStyle: "italic" }}>
            Assessed at company level — individual sites order too infrequently to measure
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
              <span className="num" style={{ fontSize: 16, fontWeight: 500, color: "var(--ui-text-hi)" }}>{fmtN(fa)}</span>
              <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>/ {fmtN(fe)} kg {s.flowWindow || "this month"}</span>
            </div>
            <ProgressBar pct={pct} severity={s.severity} />
          </>
        )}
      </div>
      <div style={{ flex: "0 0 60px" }}>
        {s.archived
          ? <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>—</span>
          : <><span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{s.daysDry ?? "—"}</span>
              {s.daysDry != null && <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}> d</span>}</>}
      </div>
      <div style={{ flex: "0 0 100px" }}>
        <span className="num" style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.5px", color: "var(--ui-text-hi)" }}>{fmt$(s.valueAtRisk)}</span>
        <div className="prose-i" style={{ fontSize: 10, color: "var(--ui-text-lo)" }}>annualised</div>
      </div>
      <div style={{ flex: "0 0 92px", display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
        <Button variant="secondary" onClick={() => nav(`/accounts/${s.customerId}`)} style={{ padding: "5px 11px", fontSize: 11 }}>Open</Button>
        <button onClick={() => nav(`/snapshot/${s.customerId}`)} className="prose-i" style={{ fontSize: 11, color: "var(--ac-indigo)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Snapshot →</button>
      </div>
    </div>
  );
}

// A site inside an expanded company. Shows volume + activity for every site;
// a per-site signal/exposure ONLY where the site was individually assessed.
// Company-level (fallback) sites carry the "assessed at company level" label so
// nobody reads their 12-month volume as a per-site assessment (§1.3).
function SiteMetricRow({ site, sig, companyLevel, customerId, selected, onSelect, nav }) {
  const flagged = !!sig && !sig.companyFallback && sig.siteId;
  return (
    <div className="lb-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
      {flagged
        ? <input type="checkbox" checked={selected.has(sig.id)} onChange={() => onSelect(sig.id)} style={{ accentColor: "var(--ac-indigo)", cursor: "pointer" }} />
        : <span style={{ width: 13 }} />}
      <div style={{ flex: "0 0 240px" }}>
        <button onClick={() => nav(`/sites/${site.siteId}`)} className="prose-i"
          style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ui-text-hi)", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
          {site.siteName || "—"}{site.archived ? " · closed / relocated" : ""}
        </button>
        <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>
          {OWN_LABEL[site.ownership] || site.ownership} · {site.machineCount || 0} machine{site.machineCount === 1 ? "" : "s"}
        </div>
      </div>
      <div style={{ flex: "0 0 150px" }}>
        {flagged
          ? <SeverityPill severity={sig.severity} label={sig.typeLabel} />
          : <span className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>{companyLevel ? "—" : (site.everOrderedBeans ? "no signal" : "never ordered")}</span>}
      </div>
      <div style={{ flex: 1, minWidth: 150, paddingRight: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span className="num" style={{ fontSize: 15, fontWeight: 500, color: "var(--ui-text-hi)" }}>{fmtN(site.kg12m)}</span>
          <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>kg last 12 months</span>
        </div>
        {companyLevel && <div className="prose-i" style={{ fontSize: 11, color: "var(--sv-watch)", fontStyle: "italic", marginTop: 3 }}>assessed at company level</div>}
      </div>
      <div style={{ flex: "0 0 60px" }}>
        {site.archived
          ? <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>—</span>
          : <><span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{site.daysDry ?? "—"}</span>
              {site.daysDry != null && <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}> d</span>}</>}
      </div>
      <div style={{ flex: "0 0 100px" }}>
        {flagged
          ? <><span className="num" style={{ fontSize: 16, fontWeight: 700, color: "var(--ui-text-hi)" }}>{fmt$(sig.valueAtRisk)}</span><div className="prose-i" style={{ fontSize: 10, color: "var(--ui-text-lo)" }}>annualised</div></>
          : <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>—</span>}
      </div>
      <div style={{ flex: "0 0 92px", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => nav(`/sites/${site.siteId}`)} className="prose-i" style={{ fontSize: 11, color: "var(--ac-indigo)", background: "none", border: "none", cursor: "pointer" }}>Open site →</button>
      </div>
    </div>
  );
}

export default function LeakBoard() {
  const nav = useNavigate();
  const [track, setTrack] = useState("all");
  const [sortBy, setSortBy] = useState("exposure");
  const [sigTypes, setSigTypes] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set(["watch"]));
  const [openCo, setOpenCo] = useState(() => new Set());
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState(() => new Set());
  const [snoozeReason, setSnoozeReason] = useState("");

  const { data, isLoading, refetch } = useQuery({ queryKey: ["leakboard", track], queryFn: () => endpoints.leakboard(track) });
  // §2 zero-spend entry point: dry-machine counts, deep-linking to the Sites engine
  const { data: zero } = useQuery({ queryKey: ["sites-dry-counts"], queryFn: () => endpoints.sites({ filter: "dry", window: 12 }) });
  const hero = data?.hero;
  const companies = data?.companies || [];
  const noData = !isLoading && data && (data.totalAccountsAll ?? 0) === 0;

  const toggleSig = (k) => { const n = new Set(sigTypes); n.has(k) ? n.delete(k) : n.add(k); setSigTypes(n); setLimit(PAGE); };
  const clearFilters = () => { setSigTypes(new Set()); setSearch(""); setTrack("all"); setLimit(PAGE); };
  const toggleCollapse = (sev) => { const n = new Set(collapsed); n.has(sev) ? n.delete(sev) : n.add(sev); setCollapsed(n); };
  const toggleCo = (cid) => { const n = new Set(openCo); n.has(cid) ? n.delete(cid) : n.add(cid); setOpenCo(n); };
  const toggleSelect = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const doBulkSnooze = async () => {
    if (!snoozeReason || selected.size === 0) return;
    for (const id of selected) { try { await endpoints.dismissSignal(id, { reason: snoozeReason }); } catch (e) { /* skip */ } }
    setSelected(new Set()); setSnoozeReason(""); refetch();
  };

  const filtered = useMemo(() => {
    let cos = companies;
    if (sigTypes.size) cos = cos.filter((c) => c.sites.some((s) => sigTypes.has(s.type)));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      cos = cos.filter((c) => (c.name || "").toLowerCase().includes(q) || c.sites.some((s) => (s.siteName || "").toLowerCase().includes(q)));
    }
    const kgShort = (c) => c.sites.reduce((a, s) => a + ((s.flowExpectedKg || 0) - (s.flowActualKg || 0)), 0);
    const daysDry = (c) => Math.max(0, ...c.sites.map((s) => s.daysDry || 0));
    const cmp = {
      exposure: (a, b) => b.combinedExposure - a.combinedExposure,
      kgshort: (a, b) => kgShort(b) - kgShort(a),
      daysdry: (a, b) => daysDry(b) - daysDry(a),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    }[sortBy];
    return [...cos].sort(cmp);
  }, [companies, sigTypes, search, sortBy]);

  const groupStats = useMemo(() => {
    const s = {}; for (const [sev] of SEV_META) s[sev] = { count: 0, exposure: 0 };
    for (const c of filtered) { const g = s[c.worstSeverity] || (s[c.worstSeverity] = { count: 0, exposure: 0 }); g.count++; g.exposure += c.combinedExposure; }
    return s;
  }, [filtered]);

  const visible = filtered.slice(0, limit);
  const bySev = useMemo(() => {
    const g = { crit: [], risk: [], watch: [] };
    for (const c of visible) (g[c.worstSeverity] || (g[c.worstSeverity] = [])).push(c);
    return g;
  }, [visible]);

  const activeChips = [
    ...[...sigTypes].map((t) => ({ label: SIGNAL_TYPES.find((s) => s[0] === t)?.[1] || t, onRemove: () => toggleSig(t) })),
    ...(search.trim() ? [{ label: `“${search.trim()}”`, onRemove: () => setSearch("") }] : []),
    ...(track !== "all" ? [{ label: track[0].toUpperCase() + track.slice(1), onRemove: () => setTrack("all") }] : []),
  ];
  const inputStyle = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "8px 12px", color: "var(--ui-text-hi)", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13 };

  if (noData) {
    return (<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div><div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Leak Board</div>
        <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>Bean flow anomalies per site, grouped by company</div></div>
      <EmptyStateCard testid="leakboard-empty" message="No data yet — run a sync to pull your Simpro history." /></div>);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* HERO */}
      <Card style={{ height: 300, borderRadius: 18, overflow: "hidden", display: "flex", padding: 0 }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: 32 }}>
          <div className="flex items-center" style={{ gap: 12 }}>
            <SeverityPill severity="crit" label="LEAKING" />
            <span className="prose-i" style={{ color: "var(--ui-text-lo)", fontSize: 14 }}>{hero?.period || "bean flow"}</span>
          </div>
          <div className="num" style={{ fontSize: 56, fontWeight: 700, letterSpacing: "-2px", color: "var(--ui-text-hi)", lineHeight: 1.05, marginTop: 8 }}>{fmtN(hero?.missingKg)} kg</div>
          <div className="prose-i" style={{ fontSize: 15, color: "var(--ui-text-mid)", marginTop: 6 }}>short over the last 90 days across {hero?.flaggedSites || 0} flagged sites</div>
          <div style={{ display: "flex", marginTop: 22 }}>
            <StatCell first label="Site-assessed" value={fmt$(hero?.siteAssessedExposure)} color="var(--sv-crit)" />
            <StatCell label="Company-level" value={fmt$(hero?.companyAssessedExposure)} color="var(--sv-watch)" />
            <StatCell label="Total at risk · annualised" value={fmt$(hero?.totalExposure)} />
          </div>
          {hero?.companyFallbacks > 0 && (
            <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 10 }}>
              {hero.companyFallbacks} companies assessed at company level (sites order too infrequently to baseline individually)
            </div>
          )}
        </div>
        <div style={{ width: 340, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", background: "transparent" }} className="hero-gradient">
          <RingGauge size={190} value={hero?.pctOfExpected || 0} centerNumber={hero?.pctOfExpected ?? 0} label="% this month" />
        </div>
      </Card>

      {/* §2 ZERO-SPEND ENTRY POINT — machines placed, no beans; jumps to the Sites engine */}
      {zero && zero.count > 0 && (
        <Card style={{ borderRadius: 14, padding: "14px 20px" }}>
          <div className="flex items-center" style={{ gap: 16, flexWrap: "wrap", justifyContent: "space-between" }}>
            <div className="flex items-center" style={{ gap: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: "var(--sv-crit)", boxShadow: "var(--glow-crit)" }} />
              <div>
                <div className="prose-i" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ui-text-hi)" }}>Zero-spend · {fmtN(zero.count)} dry machines</div>
                <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>a machine is on site but no beans ordered in 12 months</div>
              </div>
            </div>
            <div className="flex items-center" style={{ gap: 10, flexWrap: "wrap" }}>
              <button data-testid="zero-debt" onClick={() => nav("/sites?filter=dry&ownership=debt")} className="prose-i"
                style={{ fontSize: 12.5, cursor: "pointer", borderRadius: 9, padding: "7px 12px", border: "1px solid var(--sv-crit)", background: "var(--sv-crit-bg)", color: "var(--sv-crit)" }}>
                {fmtN(zero.debtCount)} contracted · debt →
              </button>
              <button data-testid="zero-winback" onClick={() => nav("/sites?filter=dry&ownership=winback")} className="prose-i"
                style={{ fontSize: 12.5, cursor: "pointer", borderRadius: 9, padding: "7px 12px", border: "1px solid var(--ac-indigo)", background: "var(--ac-indigo-soft)", color: "var(--ac-indigo-hi)" }}>
                {fmtN(zero.winBackCount)} owned / unknown · win-back →
              </button>
              <button data-testid="zero-all" onClick={() => nav("/sites?filter=dry")} className="prose-i"
                style={{ fontSize: 12, cursor: "pointer", background: "none", border: "none", color: "var(--ac-indigo)" }}>
                open zero-spend view →
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* CONTROLS */}
      <Card style={{ borderRadius: 18, padding: 0 }}>
        <div style={{ padding: "20px 26px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="flex items-center" style={{ justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div className="num" style={{ fontSize: 19, fontWeight: 500, letterSpacing: "-0.4px", color: "var(--ui-text-hi)" }}>Flagged sites</div>
              <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>Grouped by company · flow is this month, exposure is annualised</div>
            </div>
            <Segmented active={track} onChange={(k) => { setTrack(k); setLimit(PAGE); }} />
          </div>
          <div className="flex items-center" style={{ gap: 10, flexWrap: "wrap" }}>
            <input placeholder="Search company or site…" value={search} onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }} style={{ ...inputStyle, width: 220 }} />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={inputStyle}>
              {SORTS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
            </select>
            <div className="flex items-center" style={{ gap: 6, flexWrap: "wrap" }}>
              {SIGNAL_TYPES.map(([k, l]) => {
                const on = sigTypes.has(k);
                return (<button key={k} onClick={() => toggleSig(k)} className="prose-i"
                  style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", border: `1px solid ${on ? "var(--ac-indigo)" : "var(--ui-line)"}`, background: on ? "var(--ac-indigo-soft)" : "transparent", color: on ? "var(--ac-indigo-hi)" : "var(--ui-text-mid)" }}>{l}</button>);
              })}
            </div>
          </div>
          {activeChips.length > 0 && (
            <div className="flex items-center" style={{ gap: 8, flexWrap: "wrap" }}>
              {activeChips.map((c, i) => <Chip key={i} label={c.label} onRemove={c.onRemove} />)}
              <button onClick={clearFilters} className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", background: "none", border: "none", cursor: "pointer" }}>Clear all</button>
            </div>
          )}
        </div>

        {isLoading && <div className="prose-i" style={{ padding: 24, color: "var(--ui-text-lo)" }}>Loading…</div>}
        {!isLoading && filtered.length === 0 && <div className="prose-i" style={{ padding: 24, color: "var(--ui-text-lo)", borderTop: "1px solid var(--ui-line-soft)" }}>No flagged sites match these filters.</div>}

        {/* SEVERITY GROUPS */}
        {!isLoading && SEV_META.map(([sev, sevLabel]) => {
          const gs = groupStats[sev] || { count: 0, exposure: 0 };
          if (gs.count === 0) return null;
          const isCollapsed = collapsed.has(sev);
          const cos = bySev[sev] || [];
          return (
            <div key={sev}>
              <button onClick={() => toggleCollapse(sev)} className="flex items-center"
                style={{ width: "100%", justifyContent: "space-between", padding: "12px 26px", background: "var(--ui-bg)", borderTop: "1px solid var(--ui-line-soft)", borderBottom: isCollapsed ? "none" : "1px solid var(--ui-line-soft)", border: "none", cursor: "pointer" }}>
                <span className="flex items-center" style={{ gap: 10 }}>
                  <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", width: 12 }}>{isCollapsed ? "▸" : "▾"}</span>
                  <SeverityPill severity={sev} label={sevLabel} />
                  <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)" }}>{gs.count} {gs.count === 1 ? "company" : "companies"}</span>
                </span>
                <span className="num" style={{ fontSize: 15, fontWeight: 600, color: "var(--ui-text-hi)" }}>{fmt$(gs.exposure)} <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", fontWeight: 400 }}>annualised</span></span>
              </button>
              {!isCollapsed && (
                <div style={{ padding: "0 26px" }}>
                  {cos.map((co) => {
                    const all = co.allSites || [];
                    // Genuinely single-site company: one flat row, no redundant header.
                    if (all.length <= 1) {
                      return <SiteRow key={co.customerId} s={co.sites[0]} companyName={co.name} showSiteName={false}
                        selected={selected.has(co.sites[0]?.id)} onSelect={() => toggleSelect(co.sites[0]?.id)} nav={nav} />;
                    }
                    // Multi-site company (site- OR company-assessed): expandable header + all sites.
                    const isOpen = !openCo.has(`c:${co.customerId}`);  // default expanded
                    const companyLevel = co.assessmentLevel === "company";
                    const sigBySite = {};
                    (co.sites || []).forEach((s) => { if (s.siteId) sigBySite[s.siteId] = s; });
                    return (
                      <div key={co.customerId}>
                        {/* The whole header used to be ONE button that only
                            expanded and collapsed, with the site count styled
                            like a link inside it - so the only thing that
                            looked clickable did nothing but collapse the row,
                            and the company itself had no way in at all. The
                            toggle and the way in are now separate controls
                            (a button cannot legally nest inside a button). */}
                        <div className="flex items-center"
                          style={{ width: "100%", justifyContent: "space-between", gap: 14, padding: "13px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
                          <button onClick={() => toggleCo(`c:${co.customerId}`)} className="flex items-center"
                            style={{ gap: 10, flex: "1 1 auto", minWidth: 0, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                            <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", width: 10 }}>{isOpen ? "▾" : "▸"}</span>
                            <span className="prose-i" style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ui-text-hi)" }}>{co.name}</span>
                            <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>
                              {companyLevel ? `${co.siteCount} sites` : `${co.flaggedSiteCount} of ${co.siteCount} sites`}
                            </span>
                            <SeverityPill severity={co.worstSeverity} />
                            {companyLevel && <span className="prose-i" style={{ fontSize: 11, color: "var(--sv-watch)", fontStyle: "italic" }}>company-level</span>}
                          </button>
                          <span className="flex items-center" style={{ gap: 16, flex: "0 0 auto" }}>
                            <span className="num" style={{ fontSize: 16, fontWeight: 700, color: "var(--ui-text-hi)" }}>{fmt$(co.combinedExposure)}</span>
                            <button onClick={() => nav(`/accounts/${co.customerId}`)} className="prose-i"
                              style={{ fontSize: 11, color: "var(--ac-indigo)", background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}>Open account →</button>
                          </span>
                        </div>
                        {isOpen && (
                          <div style={{ paddingLeft: 22 }}>
                            {all.map((site) => (
                              <SiteMetricRow key={site.siteId} site={site} sig={sigBySite[site.siteId]}
                                companyLevel={companyLevel} customerId={co.customerId}
                                selected={selected} onSelect={toggleSelect} nav={nav} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {!isLoading && filtered.length > 0 && (
          <div className="flex items-center" style={{ justifyContent: "space-between", padding: "16px 26px", borderTop: "1px solid var(--ui-line-soft)" }}>
            <span className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-lo)" }}>Showing {Math.min(limit, filtered.length)} of {filtered.length} companies ({data?.totalFlaggedSites} flagged sites)</span>
            {limit < filtered.length && <Button variant="secondary" onClick={() => setLimit(limit + PAGE)} style={{ fontSize: 13 }}>Load {Math.min(PAGE, filtered.length - limit)} more</Button>}
          </div>
        )}
      </Card>

      {selected.size > 0 && (
        <div style={{ position: "sticky", bottom: 16, zIndex: 30, display: "flex", justifyContent: "center" }}>
          <Card style={{ borderRadius: 14, padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "var(--shadow-deep)" }}>
            <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-hi)", fontWeight: 600 }}>{selected.size} selected</span>
            <select value={snoozeReason} onChange={(e) => setSnoozeReason(e.target.value)} style={inputStyle}>
              <option value="">Snooze reason…</option>
              {SNOOZE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Button onClick={doBulkSnooze} style={{ fontSize: 13, opacity: snoozeReason ? 1 : 0.5 }}>Snooze {selected.size}</Button>
            <button onClick={() => setSelected(new Set())} className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", background: "none", border: "none", cursor: "pointer" }}>Cancel</button>
          </Card>
        </div>
      )}
    </div>
  );
}
